export type AssetSlot = 'logo' | 'hero' | 'gallery' | 'owner';

export interface OptimizedImage {
  file: File;
  width?: number;
  height?: number;
  mimeType: string;
}

const SLOT_TARGETS: Record<
  AssetSlot,
  { maxWidth: number; maxHeight: number; quality: number; square?: boolean }
> = {
  hero: { maxWidth: 1600, maxHeight: 1600, quality: 0.8 },
  gallery: { maxWidth: 1200, maxHeight: 1200, quality: 0.75 },
  owner: { maxWidth: 512, maxHeight: 512, quality: 0.8, square: true },
  logo: { maxWidth: 512, maxHeight: 512, quality: 0.85 },
};

const SKIP_UNDER_BYTES = 400 * 1024;

function baseName(name: string): string {
  return name.replace(/\.[^.]+$/, '') || 'asset';
}

function isHeic(file: File): boolean {
  const t = (file.type || '').toLowerCase();
  const n = file.name.toLowerCase();
  return (
    t === 'image/heic' ||
    t === 'image/heif' ||
    n.endsWith('.heic') ||
    n.endsWith('.heif')
  );
}

function isSvg(file: File): boolean {
  const t = (file.type || '').toLowerCase();
  return t === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
}

async function loadBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    // Fallback for browsers that reject some MIME types in createImageBitmap.
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Could not decode image'));
        el.src = url;
      });
      return await createImageBitmap(img);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

function fitContain(
  srcW: number,
  srcH: number,
  maxW: number,
  maxH: number,
): { width: number; height: number } {
  const scale = Math.min(1, maxW / srcW, maxH / srcH);
  return {
    width: Math.max(1, Math.round(srcW * scale)),
    height: Math.max(1, Math.round(srcH * scale)),
  };
}

function hasAlpha(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i]! < 255) return true;
  }
  return false;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error(`Failed to encode ${type}`));
      },
      type,
      quality,
    );
  });
}

async function encodeCanvas(
  canvas: HTMLCanvasElement,
  opts: { preferPng: boolean; quality: number; base: string },
): Promise<{ file: File; mimeType: string }> {
  if (opts.preferPng) {
    const png = await canvasToBlob(canvas, 'image/png', 1);
    return {
      file: new File([png], `${opts.base}.png`, { type: 'image/png' }),
      mimeType: 'image/png',
    };
  }

  try {
    const webp = await canvasToBlob(canvas, 'image/webp', opts.quality);
    if (webp.type === 'image/webp' || webp.size > 0) {
      // Some browsers return empty/wrong type when WebP encode is unsupported.
      if (webp.size > 0 && (webp.type === 'image/webp' || webp.type === '')) {
        const sniff = new Uint8Array(await webp.slice(0, 4).arrayBuffer());
        // RIFF....WEBP — byte 0-3 is RIFF
        if (sniff[0] === 0x52 && sniff[1] === 0x49 && sniff[2] === 0x46 && sniff[3] === 0x46) {
          return {
            file: new File([webp], `${opts.base}.webp`, { type: 'image/webp' }),
            mimeType: 'image/webp',
          };
        }
      }
    }
  } catch {
    // fall through to JPEG
  }

  const jpeg = await canvasToBlob(canvas, 'image/jpeg', opts.quality);
  return {
    file: new File([jpeg], `${opts.base}.jpg`, { type: 'image/jpeg' }),
    mimeType: 'image/jpeg',
  };
}

/**
 * Resize/compress a lead asset for demo use. SVG passes through.
 * HEIC/HEIF are rejected — convert to JPEG/PNG/WebP first.
 */
export async function optimizeImageForSlot(
  file: File,
  slot: AssetSlot = 'gallery',
): Promise<OptimizedImage> {
  if (isHeic(file)) {
    throw new Error(
      'HEIC/HEIF photos are not supported. Convert to JPEG, PNG, or WebP and try again.',
    );
  }

  if (isSvg(file)) {
    return {
      file,
      mimeType: 'image/svg+xml',
    };
  }

  const target = SLOT_TARGETS[slot];
  const bitmap = await loadBitmap(file);
  const srcW = bitmap.width;
  const srcH = bitmap.height;

  const alreadySmall =
    file.size <= SKIP_UNDER_BYTES &&
    srcW <= target.maxWidth &&
    srcH <= target.maxHeight &&
    !target.square &&
    /image\/(webp|jpeg|png)/.test(file.type || '');

  if (alreadySmall) {
    bitmap.close();
    return {
      file,
      width: srcW,
      height: srcH,
      mimeType: file.type || 'image/jpeg',
    };
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) {
    bitmap.close();
    throw new Error('Could not create canvas for image optimization');
  }

  let outW: number;
  let outH: number;

  if (target.square) {
    outW = target.maxWidth;
    outH = target.maxHeight;
    canvas.width = outW;
    canvas.height = outH;
    const scale = Math.max(outW / srcW, outH / srcH);
    const drawW = srcW * scale;
    const drawH = srcH * scale;
    const dx = (outW - drawW) / 2;
    const dy = (outH - drawH) / 2;
    ctx.drawImage(bitmap, dx, dy, drawW, drawH);
  } else {
    const fitted = fitContain(srcW, srcH, target.maxWidth, target.maxHeight);
    outW = fitted.width;
    outH = fitted.height;
    canvas.width = outW;
    canvas.height = outH;
    ctx.drawImage(bitmap, 0, 0, outW, outH);
  }

  bitmap.close();

  const preferPng = slot === 'logo' && hasAlpha(ctx, outW, outH);
  const encoded = await encodeCanvas(canvas, {
    preferPng,
    quality: target.quality,
    base: baseName(file.name),
  });

  return {
    file: encoded.file,
    width: outW,
    height: outH,
    mimeType: encoded.mimeType,
  };
}
